import { relations } from "drizzle-orm";
import {
  index,
  integer,
  primaryKey,
  sqliteTable,
  text,
  uniqueIndex,
  type AnySQLiteColumn,
} from "drizzle-orm/sqlite-core";
import { user } from "./auth";

export const series = sqliteTable("series", {
  id: text("id").primaryKey(),
  slug: text("slug").notNull().unique(),
  title: text("title").notNull(),
  originalTitle: text("original_title"),
  synopsis: text("synopsis"),
  coverR2Key: text("cover_r2_key"),
  status: text("status", {
    enum: ["ongoing", "completed", "upcoming", "hiatus", "cancelled", "unknown"],
  })
    .notNull()
    .default("unknown"),
  externalIdsJson: text("external_ids_json").notNull().default("{}"),
  createdAt: integer("created_at", { mode: "timestamp_ms" })
    .$defaultFn(() => new Date())
    .notNull(),
  updatedAt: integer("updated_at", { mode: "timestamp_ms" })
    .$defaultFn(() => new Date())
    .$onUpdate(() => /* @__PURE__ */ new Date())
    .notNull(),
});

export type Series = typeof series.$inferSelect;

export const entries = sqliteTable(
  "entries",
  {
    id: text("id").primaryKey(),
    seriesId: text("series_id")
      .notNull()
      .references(() => series.id, { onDelete: "cascade" }),
    kind: text("kind", { enum: ["anime", "manga", "novel"] }).notNull(),
    name: text("name"),
    seasonNumber: integer("season_number").notNull().default(1),
    ordinalNumber: integer("ordinal_number").notNull().default(0),
    position: integer("position").notNull(),
    status: text("status", {
      enum: ["draft", "processing", "published", "failed"],
    })
      .notNull()
      .default("draft"),
    publishedAt: integer("published_at", { mode: "timestamp_ms" }),
    createdAt: integer("created_at", { mode: "timestamp_ms" })
      .$defaultFn(() => new Date())
      .notNull(),
    updatedAt: integer("updated_at", { mode: "timestamp_ms" })
      .$defaultFn(() => new Date())
      .$onUpdate(() => /* @__PURE__ */ new Date())
      .notNull(),
  },
  (table) => [
    uniqueIndex("entries_series_kind_season_ordinal_idx").on(
      table.seriesId,
      table.kind,
      table.seasonNumber,
      table.ordinalNumber,
    ),
    uniqueIndex("entries_series_kind_position_idx").on(table.seriesId, table.kind, table.position),
    index("entries_series_kind_idx").on(table.seriesId, table.kind),
    index("entries_status_idx").on(table.status),
  ],
);

export type Entry = typeof entries.$inferSelect;

export const entryMedia = sqliteTable(
  "entry_media",
  {
    id: text("id").primaryKey(),
    entryId: text("entry_id")
      .notNull()
      .references(() => entries.id, { onDelete: "cascade" }),
    kind: text("kind", {
      enum: [
        "video_stream",
        "hls_manifest",
        "image_page",
        "webp_page",
        "markdown_body",
        "subtitle_file",
      ],
    }).notNull(),
    r2Key: text("r2_key").notNull().unique(),
    mimeType: text("mime_type").notNull(),
    byteSize: integer("byte_size"),
    width: integer("width"),
    height: integer("height"),
    durationSeconds: integer("duration_seconds"),
    sortOrder: integer("sort_order").notNull().default(0),
    metaJson: text("meta_json").notNull().default("{}"),
    sourceMediaId: text("source_media_id").references((): AnySQLiteColumn => entryMedia.id, {
      onDelete: "set null",
    }),
    createdAt: integer("created_at", { mode: "timestamp_ms" })
      .$defaultFn(() => new Date())
      .notNull(),
    updatedAt: integer("updated_at", { mode: "timestamp_ms" })
      .$defaultFn(() => new Date())
      .$onUpdate(() => /* @__PURE__ */ new Date())
      .notNull(),
  },
  (table) => [
    index("entry_media_entry_kind_idx").on(table.entryId, table.kind),
    index("entry_media_entry_sort_idx").on(table.entryId, table.sortOrder),
  ],
);

export const inviteCodes = sqliteTable(
  "invite_codes",
  {
    id: text("id").primaryKey(),
    code: text("code").notNull().unique(),
    role: text("role", { enum: ["user", "friend", "admin"] })
      .notNull()
      .default("friend"),
    maxUses: integer("max_uses").notNull().default(1),
    useCount: integer("use_count").notNull().default(0),
    createdBy: text("created_by")
      .notNull()
      .references(() => user.id, { onDelete: "cascade" }),
    usedBy: text("used_by").references(() => user.id, { onDelete: "set null" }),
    usedAt: integer("used_at", { mode: "timestamp_ms" }),
    expiresAt: integer("expires_at", { mode: "timestamp_ms" }),
    createdAt: integer("created_at", { mode: "timestamp_ms" })
      .$defaultFn(() => new Date())
      .notNull(),
    updatedAt: integer("updated_at", { mode: "timestamp_ms" })
      .$defaultFn(() => new Date())
      .$onUpdate(() => /* @__PURE__ */ new Date())
      .notNull(),
  },
  (table) => [index("invite_codes_created_by_idx").on(table.createdBy)],
);

export const mediaRequests = sqliteTable(
  "media_requests",
  {
    id: text("id").primaryKey(),
    userId: text("user_id")
      .notNull()
      .references(() => user.id, { onDelete: "cascade" }),
    title: text("title").notNull(),
    kind: text("kind", {
      enum: ["anime", "manga", "novel", "manhua", "manhwa", "ln"],
    }).notNull(),
    sourceUrl: text("source_url"),
    notes: text("notes"),
    status: text("status", {
      enum: ["pending", "approved", "rejected", "fulfilled"],
    })
      .notNull()
      .default("pending"),
    reviewedBy: text("reviewed_by").references(() => user.id, {
      onDelete: "set null",
    }),
    reviewedAt: integer("reviewed_at", { mode: "timestamp_ms" }),
    fulfilledSeriesId: text("fulfilled_series_id").references(() => series.id, {
      onDelete: "set null",
    }),
    createdAt: integer("created_at", { mode: "timestamp_ms" })
      .$defaultFn(() => new Date())
      .notNull(),
    updatedAt: integer("updated_at", { mode: "timestamp_ms" })
      .$defaultFn(() => new Date())
      .$onUpdate(() => /* @__PURE__ */ new Date())
      .notNull(),
  },
  (table) => [
    index("media_requests_user_idx").on(table.userId),
    index("media_requests_status_idx").on(table.status),
  ],
);

export const userFavorites = sqliteTable(
  "user_favorites",
  {
    userId: text("user_id")
      .notNull()
      .references(() => user.id, { onDelete: "cascade" }),
    seriesId: text("series_id")
      .notNull()
      .references(() => series.id, { onDelete: "cascade" }),
    createdAt: integer("created_at", { mode: "timestamp_ms" })
      .$defaultFn(() => new Date())
      .notNull(),
  },
  (table) => [primaryKey({ columns: [table.userId, table.seriesId] })],
);

export const userEntryProgress = sqliteTable(
  "user_entry_progress",
  {
    userId: text("user_id")
      .notNull()
      .references(() => user.id, { onDelete: "cascade" }),
    entryId: text("entry_id")
      .notNull()
      .references(() => entries.id, { onDelete: "cascade" }),
    seriesId: text("series_id")
      .notNull()
      .references(() => series.id, { onDelete: "cascade" }),
    isCompleted: integer("is_completed", { mode: "boolean" }).default(false).notNull(),
    progressPercent: integer("progress_percent").default(0).notNull(),
    resumeTimestampSeconds: integer("resume_timestamp_seconds"),
    lastSeenAt: integer("last_seen_at", { mode: "timestamp_ms" })
      .$defaultFn(() => new Date())
      .notNull(),
  },
  (table) => [
    primaryKey({ columns: [table.userId, table.entryId] }),
    index("user_entry_progress_user_series_idx").on(table.userId, table.seriesId),
    index("user_entry_progress_user_last_seen_idx").on(table.userId, table.lastSeenAt),
  ],
);

export const entryComments = sqliteTable(
  "entry_comments",
  {
    id: text("id").primaryKey(),
    entryId: text("entry_id")
      .notNull()
      .references(() => entries.id, { onDelete: "cascade" }),
    parentId: text("parent_id").references((): AnySQLiteColumn => entryComments.id, {
      onDelete: "cascade",
    }),
    userId: text("user_id")
      .notNull()
      .references(() => user.id, { onDelete: "cascade" }),
    body: text("body").notNull(),
    resolved: integer("resolved", { mode: "boolean" }).notNull().default(false),
    agentReply: text("agent_reply"),
    startOffset: integer("start_offset"),
    endOffset: integer("end_offset"),
    selectedText: text("selected_text"),
    contextPrefix: text("context_prefix"),
    contextSuffix: text("context_suffix"),
    createdAt: integer("created_at", { mode: "timestamp_ms" })
      .$defaultFn(() => new Date())
      .notNull(),
    updatedAt: integer("updated_at", { mode: "timestamp_ms" })
      .$defaultFn(() => new Date())
      .$onUpdate(() => new Date())
      .notNull(),
  },
  (table) => [
    index("entry_comments_entry_idx").on(table.entryId),
    index("entry_comments_user_idx").on(table.userId),
    index("entry_comments_resolved_idx").on(table.resolved),
    index("entry_comments_entry_offset_idx").on(table.entryId, table.startOffset),
  ],
);

export type EntryComment = typeof entryComments.$inferSelect;

export const pipelineNotes = sqliteTable(
  "pipeline_notes",
  {
    id: text("id").primaryKey(),
    key: text("key").notNull().unique(),
    value: text("value").notNull(),
    createdBy: text("created_by").references(() => user.id, { onDelete: "set null" }),
    createdAt: integer("created_at", { mode: "timestamp_ms" })
      .$defaultFn(() => new Date())
      .notNull(),
    updatedAt: integer("updated_at", { mode: "timestamp_ms" })
      .$defaultFn(() => new Date())
      .$onUpdate(() => new Date())
      .notNull(),
  },
  (table) => [index("pipeline_notes_key_idx").on(table.key)],
);

export type PipelineNote = typeof pipelineNotes.$inferSelect;

export const entrySearchContent = sqliteTable(
  "entry_search_content",
  {
    entryId: text("entry_id")
      .primaryKey()
      .references(() => entries.id, { onDelete: "cascade" }),
    seriesId: text("series_id")
      .notNull()
      .references(() => series.id, { onDelete: "cascade" }),
    plainText: text("plain_text").notNull(),
    wordCount: integer("word_count").notNull().default(0),
    indexedAt: integer("indexed_at", { mode: "timestamp_ms" })
      .$defaultFn(() => new Date())
      .notNull(),
  },
  (table) => [index("entry_search_series_idx").on(table.seriesId)],
);

export type EntrySearchContent = typeof entrySearchContent.$inferSelect;

export const seriesRelations = relations(series, ({ many }) => ({
  entries: many(entries),
  favorites: many(userFavorites),
  entryProgress: many(userEntryProgress),
  mediaRequests: many(mediaRequests),
}));

export const entriesRelations = relations(entries, ({ one, many }) => ({
  series: one(series, {
    fields: [entries.seriesId],
    references: [series.id],
  }),
  media: many(entryMedia),
  progress: many(userEntryProgress),
  comments: many(entryComments),
}));

export const entryMediaRelations = relations(entryMedia, ({ one, many }) => ({
  entry: one(entries, {
    fields: [entryMedia.entryId],
    references: [entries.id],
  }),
  sourceMedia: one(entryMedia, {
    fields: [entryMedia.sourceMediaId],
    references: [entryMedia.id],
    relationName: "source_media",
  }),
  derivedMedia: many(entryMedia, { relationName: "source_media" }),
}));

export const inviteCodesRelations = relations(inviteCodes, ({ one }) => ({
  creator: one(user, {
    fields: [inviteCodes.createdBy],
    references: [user.id],
  }),
  usedByUser: one(user, {
    fields: [inviteCodes.usedBy],
    references: [user.id],
  }),
}));

export const mediaRequestsRelations = relations(mediaRequests, ({ one }) => ({
  requester: one(user, {
    fields: [mediaRequests.userId],
    references: [user.id],
  }),
  reviewer: one(user, {
    fields: [mediaRequests.reviewedBy],
    references: [user.id],
  }),
  fulfilledSeries: one(series, {
    fields: [mediaRequests.fulfilledSeriesId],
    references: [series.id],
  }),
}));

export const userFavoritesRelations = relations(userFavorites, ({ one }) => ({
  user: one(user, {
    fields: [userFavorites.userId],
    references: [user.id],
  }),
  series: one(series, {
    fields: [userFavorites.seriesId],
    references: [series.id],
  }),
}));

export const userEntryProgressRelations = relations(userEntryProgress, ({ one }) => ({
  user: one(user, {
    fields: [userEntryProgress.userId],
    references: [user.id],
  }),
  entry: one(entries, {
    fields: [userEntryProgress.entryId],
    references: [entries.id],
  }),
  series: one(series, {
    fields: [userEntryProgress.seriesId],
    references: [series.id],
  }),
}));

export const entryCommentsRelations = relations(entryComments, ({ one, many }) => ({
  entry: one(entries, {
    fields: [entryComments.entryId],
    references: [entries.id],
  }),
  author: one(user, {
    fields: [entryComments.userId],
    references: [user.id],
  }),
  parent: one(entryComments, {
    fields: [entryComments.parentId],
    references: [entryComments.id],
    relationName: "parent_comment",
  }),
  replies: many(entryComments, { relationName: "parent_comment" }),
}));

export const pipelineNotesRelations = relations(pipelineNotes, ({ one }) => ({
  creator: one(user, {
    fields: [pipelineNotes.createdBy],
    references: [user.id],
  }),
}));
